import { switchMap } from "rxjs/operators";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { BeforeNextStepEventHandler } from "../../common/wizard/before-next-step.event";
import { ActivatedRoute, Params } from "@angular/router";
import { AutopilotRepository } from "../../../domain/autopilot-repository";
import { UserService } from "../../../domain/user-service";
import { WizardStepComponent } from "../../common/wizard/wizard-step.component";

import libraryStore from "../../../services/stores/library.store";
import { LibraryTypes } from "../../../domain/enums";
import { GraphicsRepository } from "../../../domain/graphics-repository";
import { Logger } from "../../../infrastructure/logger";
import { UI } from "../../../services/ui.service";
import { NavigationItemModel } from "../../common/sideNav/navigationItem.model";
import { SearchService } from "../../../services/search.service";
import { ApplyState } from "../../common/wizard/wizard-nav.component";
import { GraphicsInfo } from "../../../domain/graphics-info";
import { Channel } from "../../../domain/channel";
import { AutopilotRule } from "../../../domain/rule";

@Component({
  selector: "icn-graphics-rule-wizard",
  templateUrl: "./autopilot-rule-wizard.component.html",
  styleUrls: ["./autopilot-rule-wizard.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutopilotRuleWizardComponent implements OnInit, OnDestroy {
  constructor(
    private userService: UserService,
    private rulesRepository: AutopilotRepository,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private graphicsRepository: GraphicsRepository,
    private logger: Logger,
    private ui: UI,
    private element: ElementRef,
    private searchService: SearchService,
  ) {}

  mamSteps = [];
  libraryStore = libraryStore;
  rule: AutopilotRule;
  edit = false;

  filterOpen = false;
  filterTranslateValue = "";
  currentChannel: Channel;
  applyState: ApplyState = ApplyState.Unknown;

  navigationItems: NavigationItemModel[] = [
    //(new NavigationItemModel(LibraryTypes.VideoRules, 'rule-icon', 'video rules')),
    new NavigationItemModel(
      LibraryTypes.AutopilotRules,
      "rule-icon",
      "autopilot rules",
    ),
    new NavigationItemModel(LibraryTypes.Graphics, "graphics-icon", "graphics"),
    new NavigationItemModel(LibraryTypes.Video, "graphics-icon", "media"),
  ];

  ngOnInit() {
    this.userService.getCurrentChannel().then((channel) => {
      this.currentChannel = channel;
    });
    this.route.params
      .pipe(
        switchMap((params: Params) => {
          this.rule = null;

          if (!params["ruleId"]) {
            return Promise.resolve({
              rule: new AutopilotRule(undefined, null, 1, false),
            });
          }

          let rule: AutopilotRule;
          return this.userService
            .getCurrentChannel()
            .then((channel) => {
              this.currentChannel = channel;
              return this.rulesRepository.getRule(params["ruleId"]);
            })
            .then((result) => {
              rule = result;
              this.edit = true;
              return { rule: rule };
            })
            .catch((err) => {
              this.logger.error("error loading graphics rule. Err: " + err);
              throw err;
            });
        }),
      )
      .subscribe((rule: { rule: AutopilotRule }) => {
        if (!rule) {
          return;
        }

        this.rule = rule.rule;
        this.changeDetectorRef.markForCheck();
      });

    this.mamSteps = [
      [LibraryTypes.AutopilotRules, LibraryTypes.Graphics],
      [LibraryTypes.AutopilotRules, LibraryTypes.AutopilotRules],
    ];

    window.addEventListener(
      "resize",
      (event) => {
        this.setupFilterLeftPosition();
        this.filterOpen = false;
      },
      false,
    );

    this.setupFilterLeftPosition();

    this.ui.listen(
      this.ui.EVENTS.LibraryFilterToggle,
      this.onLibraryFilterToggle.bind(this),
    );

    this.searchService.searchMode = "graphics";
    this.searchService.subjectModeNext();
  }

  ngOnDestroy(): void {
    this.searchService.searchMode = "video";
  }

  onLibraryFilterToggle(event) {
    this.filterOpen = !this.filterOpen;

    const $filter = this.element.nativeElement.querySelector("filter");

    this.ui.setCssProperty($filter, {
      transform:
        "translateX(" +
        (this.filterOpen ? "0" : "-" + this.filterTranslateValue) +
        ")",
    });
  }

  setupFilterLeftPosition() {
    const $filter = this.element.nativeElement.querySelector("filter");

    if ($filter == null || !$filter) {
      return;
    }

    const asideWidth = this.ui.getComputedElement(
      this.element.nativeElement.querySelector("aside"),
      "width",
    );
    const tabsWidth = this.ui.getComputedElement(
      document.querySelector("icn-sidebar-nav"),
      "width",
    );

    const filterWidth = this.ui.getComputedElement($filter, "width");

    this.filterTranslateValue = filterWidth;

    const leftPosition = parseFloat(asideWidth) + parseFloat(tabsWidth);

    this.ui.setCssProperty($filter, {
      left: `${leftPosition}px`,
      transform: "translateX(-" + this.filterTranslateValue + ")",
    });
  }

  getStepsTitle() {
    if (!this.rule.id) {
      return `Create Your Autopilot Rule`;
    } else {
      return `Edit Your Autopilot Rule`;
    }
  }

  getDoneTitle() {
    return `Good Job!
Your Rule Is Good To Go`;
  }

  onApply() {
    console.log("onApply", this.rule);
    const promise = this.rule.id
      ? (x) => this.rulesRepository.updateRule(x)
      : (x) => this.rulesRepository.createRule(x);
    promise(this.rule)
      .then(
        () => {
          this.activateApplyNotification(ApplyState.Succeeded);
        },
        () => {
          this.activateApplyNotification(ApplyState.Failed);
        },
      )
      .catch((err) => {
        this.activateApplyNotification(ApplyState.Failed);
        console.error(err);
      });
  }

  activateApplyNotification(state: ApplyState) {
    this.applyState = state;
    this.changeDetectorRef.markForCheck();

    setTimeout(() => {
      this.applyState = ApplyState.Unknown;
      this.changeDetectorRef.markForCheck();
    }, 2500);
  }

  onFinish(beforeNextStepEvent: BeforeNextStepEventHandler) {
    const promise = this.rule.id
      ? (x) => this.rulesRepository.updateRule(x)
      : (x) => this.rulesRepository.createRule(x);

    promise(this.rule)
      .then(() => {
        beforeNextStepEvent.next();
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        console.error(err);
        beforeNextStepEvent.cancel();
      });
  }

  onNextHandler(event) {
    const previousStep: WizardStepComponent = event.previousStep;
    this.changeMamIfNeeded(previousStep);
  }

  onBackHandler(event) {
    const previousStep: WizardStepComponent = event.previousStep;
    this.changeMamIfNeeded(previousStep);
  }

  changeMamIfNeeded(stepDetails: WizardStepComponent) {
    if (
      this.mamSteps[stepDetails.stepIndex] &&
      this.mamSteps[stepDetails.stepIndex][
        stepDetails.currentSubstep.substepIndex
      ]
    ) {
      const libraryType =
        this.mamSteps[stepDetails.stepIndex][
          stepDetails.currentSubstep.substepIndex
        ];

      this.libraryStore.setLibraryType(libraryType);

      this.changeDetectorRef.markForCheck();
    }
  }
}
